import React, { useState, useEffect, useRef, forwardRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiConfig } from "../../../../utils/apiConfig";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import styled from "styled-components";
import SubPageLayout from "../../../../components/shared/SubPageLayout";

import logger from '../../../../utils/logger';

const EditorContainer = styled.div`
  .ql-editor {
    min-height: 200px;
    max-height: 400px;
    overflow-y: auto;
    color: #000000 !important;
    background: ${({ theme }) => theme.isDarkMode ? theme.colors.surface.default : theme.colors.surface.paper};
    padding: 16px;
  }

  .ql-container.ql-snow .ql-editor {
    color: #000000 !important;
  }

  .ql-container.ql-snow .ql-editor * {
    color: #000000 !important;
  }

  .ql-container.ql-snow .ql-editor p {
    color: #000000 !important;
  }

  .ql-snow .ql-editor {
    color: #000000 !important;
  }

  .ql-snow .ql-editor p {
    color: #000000 !important;
  }

  .quill {
    background: ${({ theme }) => theme.isDarkMode ? theme.colors.surface.default : theme.colors.surface.paper};
    border-radius: 8px;
    box-shadow: ${({ theme }) => theme.isDarkMode ? "none" : theme.shadows.md};
  }

  .ql-toolbar.ql-snow {
    border: 1px solid ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[700] : theme.colors.neutral[200]};
    border-bottom: none;
    border-radius: 8px 8px 0 0;
    background: ${({ theme }) => theme.isDarkMode ? theme.colors.surface.default : theme.colors.surface.paper};
    padding: 12px;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  .ql-container.ql-snow {
    border: 1px solid ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[700] : theme.colors.neutral[200]};
    border-radius: 0 0 8px 8px;
    min-height: 200px;
    background: ${({ theme }) => theme.isDarkMode ? theme.colors.surface.default : theme.colors.surface.paper};
    box-shadow: ${({ theme }) => theme.isDarkMode ? "none" : theme.shadows.md};
  }

  .ql-toolbar button {
    background: ${({ theme }) => theme.isDarkMode ? theme.colors.surface.default : theme.colors.surface.paper};
    border: 1px solid ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[700] : theme.colors.neutral[200]};
    color: ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[100] : theme.colors.neutral[900]};
    padding: 6px 10px;
    font-size: 16px;
    cursor: pointer;
    border-radius: 6px;
    transition: all 0.2s;
  }

  .ql-toolbar button:hover {
    background: ${({ theme }) => theme.isDarkMode ? theme.colors.surface.default : theme.colors.surface.paper};
    border-color: ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[100] : theme.colors.neutral[900]};
  }

  .file-input-container {
    margin-top: 16px;
  }

  .file-input-button {
    display: inline-flex;
    align-items: center;
    padding: 8px 16px;
    background: ${({ theme }) => theme.isDarkMode ? theme.colors.surface.default : theme.colors.surface.paper};
    border: 1px solid ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[700] : theme.colors.neutral[200]};
    border-radius: 6px;
    color: ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[100] : theme.colors.neutral[900]};
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .file-list {
    margin-top: 8px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .file-item {
    display: inline-flex;
    align-items: center;
    padding: 4px 8px;
    background: ${({ theme }) => theme.isDarkMode ? theme.colors.surface.default : theme.colors.surface.paper};
    border: 1px solid ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[700] : theme.colors.neutral[200]};
    border-radius: 4px;
    font-size: 12px;
    color: ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[100] : theme.colors.neutral[900]};
  }

  .file-item button {
    margin-left: 6px;
    padding: 0;
    background: none;
    border: none;
    color: ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[100] : theme.colors.neutral[900]};
    cursor: pointer;
  }
`;

const QuillEditor = forwardRef(({ value, onChange }, ref) => {
  return (
    <ReactQuill
      ref={ref}
      theme="snow"
      value={value}
      onChange={onChange}
      modules={{
        toolbar: [
          [{ size: ["small", false, "large", "huge"] }],
          ["bold", "italic", "underline", "strike"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link"],
        ],
      }}
      preserveWhitespace={true}
    />
  );
});

QuillEditor.displayName = "QuillEditor";

const modules = {
  toolbar: [
    [{ size: ["small", false, "large", "huge"] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
  ],
};

const formats = [
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "link",
];

const PostAnnualReviewActionItems = () => {
  const { householdId, year, version } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDraftEmail, setShowDraftEmail] = useState(false);
  const [emailContent, setEmailContent] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const quillRef = useRef();

  const [formData, setFormData] = useState({
    interestedRothConversion: false,
    rothComplete: false,
    clientRMD: false,
    clientRMDComplete: false,
    riskAnalysisDesired: false,
    riskAnalysisComplete: false,
    portfolioAnalysisDesired: false,
    portfolioAnalysisComplete: false,
    reviewComplete: false,
    taxFilingStatus: "Married Filing Jointly",
    conversionFrom: "Charles Schwab",
    conversionTo: "Charles Schwab",
    conversionAccountFrom: "",
    conversionAccountTo: "",
    conversionAmount: "$0.00",
    federalTaxWithholding: 0,
  });

  // Add currency formatter
  const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        logger.info("Initial load - Token:", !!token);
        logger.info("URL params:", { householdId, year, version });

        if (!token) {
          logger.info("No token found, redirecting to login");
          navigate("/");
          return;
        }

        // Fetch existing data if available
        const url = `/advisor-portal/client-database/household-profile/${householdId}/ar-profile/${year}/${version}/post-review`;
        logger.info("Attempting to fetch:", url);

        const response = await apiConfig.api.get(url).catch((error) => {
          logger.info("API error:", {
            status: error.response?.status,
            message: error.response?.data?.message,
            headers: error.response?.headers,
            url: url,
          });
          throw error;
        });

        logger.info("Response received:", response.data);

        if (response.data) {
          // Map the response data to our form fields
          const profileData = response.data.formData?.[0]?.data || {};
          setFormData((prev) => ({
            ...prev,
            ...profileData,
            taxFilingStatus:
              profileData.taxInfo?.filingStatus || prev.taxFilingStatus,
          }));
        }

        setLoading(false);
      } catch (err) {
        logger.error("Error fetching data:", err);
        if (err.response?.status === 401) {
          logger.info("Token expired or invalid, redirecting to login");
          navigate("/");
        } else {
          setError(err.message || "Failed to fetch data");
        }
        setLoading(false);
      }
    };

    fetchData();
  }, [householdId, year, version, navigate]);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    if (name === "conversionAmount") {
      // Only allow numbers and decimal point while typing
      const numericValue = value.replace(/[^\d.]/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: numericValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles((prev) => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRegenerateResponse = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        logger.info("No token found, redirecting to login");
        navigate("/");
        return;
      }

      const response = await apiConfig.api.post(
        `/advisor-portal/client-database/household-profile/${householdId}/ar-profile/${year}/${version}/post-review/regenerate-response`,
        { formData },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data?.content) {
        setEmailContent(response.data.content);
      }
    } catch (err) {
      logger.error("Error regenerating response:", err);
      if (err.response?.status === 401) {
        logger.info("Token expired or invalid, redirecting to login");
        localStorage.removeItem("token");
        navigate("/");
        return;
      }
      alert(
        "Error regenerating response: " +
          (err.response?.data?.message || err.message),
      );
    }
  };

  const handleToggleDraftEmail = () => {
    setShowDraftEmail((prev) => !prev);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        logger.info("No token found, redirecting to login");
        navigate("/");
        return;
      }

      const formDataToSend = new FormData();
      formDataToSend.append("data", JSON.stringify(formData));
      formDataToSend.append("emailContent", emailContent);
      selectedFiles.forEach((file) => {
        formDataToSend.append("attachments", file);
      });

      const response = await apiConfig.api.post(
        `/advisor-portal/client-database/household-profile/${householdId}/ar-profile/${year}/${version}/post-review`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data) {
        alert("Data saved successfully");
      }
    } catch (err) {
      logger.error("Error saving data:", err);
      if (err.response?.status === 401) {
        logger.info("Token expired or invalid, redirecting to login");
        localStorage.removeItem("token");
        navigate("/");
        return;
      }
      alert(
        "Error saving data: " + (err.response?.data?.message || err.message),
      );
    }
  };

  const handleGeneratePDF = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        logger.info("No token found, redirecting to login");
        navigate("/");
        return;
      }

      const response = await apiConfig.api.post(
        `/advisor-portal/client-database/household-profile/${householdId}/ar-profile/${year}/${version}/post-review/generate-pdf`,
        {
          conversion_from: formData.conversionFrom,
          account_number_from: formData.conversionAccountFrom,
          conversion_to: formData.conversionTo,
          account_number_to: formData.conversionAccountTo,
          advisor_name: localStorage.getItem("advisorName") || "",
          client_name: localStorage.getItem("clientName") || "",
          conversion_amount: formData.conversionAmount,
          federal_tax_withholding: formData.federalTaxWithholding,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          responseType: "blob",
        },
      );

      // Create a blob from the PDF stream
      const file = new Blob([response.data], { type: "application/pdf" });

      // Create a URL for the blob
      const fileURL = URL.createObjectURL(file);

      // Open the PDF in a new window
      window.open(fileURL, "_blank");
    } catch (err) {
      logger.error("Error generating PDF:", err);
      alert(
        "Error generating PDF: " + (err.response?.data?.message || err.message),
      );
    }
  };

  // Format currency for display
  const formatCurrencyForDisplay = (value) => {
    if (!value) return "";
    const numericValue = parseFloat(value);
    return isNaN(numericValue) ? "" : currencyFormatter.format(numericValue);
  };

  const breadcrumbItems = [
    { label: "Advisor Portal", path: "/advisor-portal" },
    { label: "Client Database", path: "/advisor-portal/client-database" },
    {
      label: "Household Profile",
      path: `/advisor-portal/client-database/household-profile/${householdId}`,
    },
    {
      label: "Annual Review",
      path: `/advisor-portal/client-database/household-profile/${householdId}/ar-profile/${year}/${version}`,
    },
    {
      label: "Post Review",
      path: `/advisor-portal/client-database/household-profile/${householdId}/ar-profile/${year}/${version}/post-review`,
    },
  ];

  if (loading) {
    return (
      <SubPageLayout title="Loading..." breadcrumbItems={breadcrumbItems}>
        <div className="text-gray-700 dark:text-gray-300">Loading...</div>
      </SubPageLayout>
    );
  }

  if (error) {
    return (
      <SubPageLayout title="Error" breadcrumbItems={breadcrumbItems}>
        <div className="text-red-500">Error: {error}</div>
      </SubPageLayout>
    );
  }

  return (
    <SubPageLayout
      title="Post Annual Review Action Items"
      subtitle="Review summary, send recap email, and generate documents"
      breadcrumbItems={breadcrumbItems}
    >
      <div className="bg-surface-default dark:bg-surface-default shadow rounded p-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
          Annual Review Summary
        </h2>
        <p className="mb-6 text-gray-900 dark:text-gray-100">
          Here you can review your summary, send the recap email, and generate
          the Conversion Disclosure PDF.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Annual Review Status Section */}
            <div className="bg-gray-50 rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Annual Review Status
              </h3>
              <div className="space-y-3">
                {[
                  {
                    label: "Interested in Roth Conversion",
                    name: "interestedRothConversion",
                  },
                  {
                    label: "Roth IRA Conversion Complete",
                    name: "rothComplete",
                  },
                  { label: "Client RMD Requirement", name: "clientRMD" },
                  {
                    label: "Client RMD Requirement Completed",
                    name: "clientRMDComplete",
                  },
                  {
                    label: "Updated Risk Tolerance Analysis Desired",
                    name: "riskAnalysisDesired",
                  },
                  {
                    label: "Updated Risk Tolerance Analysis Complete",
                    name: "riskAnalysisComplete",
                  },
                  {
                    label: "Updated Portfolio Analysis Desired",
                    name: "portfolioAnalysisDesired",
                  },
                  {
                    label: "Updated Portfolio Analysis Complete",
                    name: "portfolioAnalysisComplete",
                  },
                  { label: "Annual Review Complete", name: "reviewComplete" },
                ].map(({ label, name }) => (
                  <label key={name} className="flex items-start space-x-2">
                    <input
                      type="checkbox"
                      name={name}
                      className="mt-1 form-checkbox h-4 w-4 text-blue-600 border-gray-300 rounded"
                      checked={formData[name]}
                      onChange={handleChange}
                    />
                    <span className="text-gray-900 dark:text-gray-100">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Recap & Document Generation Factors Section */}
            <div className="bg-gray-50 rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Recap & Document Generation Factors
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                      Tax Filing Status:
                    </label>
                    <select
                      name="taxFilingStatus"
                      className="w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-black"
                      value={formData.taxFilingStatus}
                      onChange={handleChange}
                    >
                      {[
                        "Single",
                        "Married Filing Jointly",
                        "Head of Household",
                      ].map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                        Conversion From:
                      </label>
                      <input
                        type="text"
                        name="conversionFrom"
                        className="w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-black"
                        value={formData.conversionFrom}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                        Conversion To:
                      </label>
                      <input
                        type="text"
                        name="conversionTo"
                        className="w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-black"
                        value={formData.conversionTo}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                        Conversion Account # From:
                      </label>
                      <input
                        type="text"
                        name="conversionAccountFrom"
                        className="w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-black"
                        placeholder="Search by name or account number"
                        value={formData.conversionAccountFrom}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                        Conversion Account # To:
                      </label>
                      <input
                        type="text"
                        name="conversionAccountTo"
                        className="w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-black"
                        placeholder="Search by name or account number"
                        value={formData.conversionAccountTo}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                        Conversion Amount:
                      </label>
                      <input
                        type="text"
                        name="conversionAmount"
                        className="w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-black"
                        value={formData.conversionAmount}
                        onChange={handleChange}
                        onBlur={(e) => {
                          const value = e.target.value.replace(/[^\d.]/g, "");
                          const numericValue = parseFloat(value) || 0;
                          setFormData((prev) => ({
                            ...prev,
                            conversionAmount:
                              formatCurrencyForDisplay(numericValue),
                          }));
                        }}
                        onFocus={(e) => {
                          // Remove formatting when focusing on the input
                          const value = e.target.value.replace(/[^0-9.]/g, "");
                          setFormData((prev) => ({
                            ...prev,
                            conversionAmount: value || "0",
                          }));
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                        Federal Tax Withholding (%):
                      </label>
                      <select
                        name="federalTaxWithholding"
                        className="w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-black"
                        value={formData.federalTaxWithholding}
                        onChange={handleChange}
                      >
                        {[...Array(51).keys()].map((i) => (
                          <option key={i} value={i}>
                            {i}%
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {showDraftEmail && (
            <div className="mt-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Draft Recap Email
                </h3>
                <button
                  type="button"
                  onClick={handleRegenerateResponse}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Regenerate Response
                </button>
              </div>
              <EditorContainer>
                <ReactQuill
                  theme="snow"
                  value={emailContent}
                  onChange={setEmailContent}
                  modules={modules}
                  formats={formats}
                  bounds={".quill"}
                  placeholder="Email content will appear here..."
                />

                <div className="file-input-container">
                  <label className="file-input-button">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Attach Files
                    <input
                      type="file"
                      multiple
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>

                  {selectedFiles.length > 0 && (
                    <div className="file-list">
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="file-item">
                          {file.name}
                          <button onClick={() => removeFile(index)}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </EditorContainer>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-8 flex justify-center space-x-4">
            <button
              type="button"
              onClick={() =>
                navigate(
                  `/advisor-portal/client-database/household-profile/${householdId}/ar-profile/${year}/${version}`,
                )
              }
              className="px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
            >
              Edit Annual Review Profile
            </button>
            <button
              type="button"
              onClick={handleToggleDraftEmail}
              className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
            >
              {showDraftEmail
                ? "Hide Draft Recap Email"
                : "Create Draft Recap Email"}
            </button>
            <button
              type="button"
              onClick={handleGeneratePDF}
              className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
            >
              Generate Roth Conversion Disclosure
            </button>
          </div>
        </form>
      </div>
    </SubPageLayout>
  );
};

export default PostAnnualReviewActionItems;
